import React from 'react';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import { Grid, Form } from 'semantic-ui-react';
import moment from 'moment';
import { connect } from 'react-redux';
import _ from 'lodash';

import {
  InputField,
  Select,
  DatePickerField,
  ReduxFormButtonGroup,
} from 'Common/SemanticWrapper';
import { InputMode, PROVISION2_FORM_NAME } from 'Common/FormEnum';
import { Provision2FieldEnum as Fields } from 'Common/FieldEnum';
import {
  createFieldsValidator,
  isDateValid,
  hasFormErrors,
} from 'Common/FormHelper';
import { MealTypeEnum } from 'Common/Constants/Enumeration/MealTypesEnum';
import { errorToast } from 'Common/ToastHelper';
import { SiteComponent } from 'Common/SiteComponent';
import { AlternateProvisionTypeEnum } from 'Common/Constants/Enumeration/AlternateProvisionTypeEnum';
import { DATE_FORMAT_FOR_UI } from 'Common/Constants';
import * as CommonStateSelectors from 'State/selectors';

import * as Provision2Selectors from '../state/selectors';
import * as Provision2Actions from '../state/actions';

const currentDate = moment();

class Provision2Form extends React.Component {
  state = {
    membership: null,
    membershipValidate: null,
  };

  componentDidUpdate(prevProps) {
    const { resetError, resetSection, provision2Error } = this.props;
    if (errorToast(this.props, prevProps, 'provision2Error')) {
      const message = _.get(provision2Error, 'Message', '');
      if (message.includes('Participation cannot exceed membership')) {
        const membership = Number(message.split('.')[2].replace(/\D/g, ''));
        // Defining a redux form validate function inside render function leads to infinite loop, need to create dynamic validate functions in state
        this.setState({
          membership,
          membershipValidate: (value) => {
            if (membership && value > membership) {
              return `Total participation cannot exceed ${membership}.`;
            }
            return undefined;
          },
        });
      }
      resetSection(Fields.Free, Fields.Reduced, Fields.Paid);
      resetError();
    }
  }

  resetParticipationValidation = () => {
    this.setState({ membership: null, membershipValidate: null });
  };

  isSiteBreakfastOnly = (siteId) => {
    const { sitesMap } = this.props;

    return (
      siteId &&
      sitesMap[siteId].AlternateProvisionTypeId ===
        AlternateProvisionTypeEnum.Prov2_Non_Base_Breakfast_Only
    );
  };

  getMealTypeOptions = () => {
    return this.isSiteBreakfastOnly(this.props.siteId)
      ? [{ value: MealTypeEnum.Breakfast, label: 'Breakfast' }]
      : [
          { value: MealTypeEnum.Breakfast, label: 'Breakfast' },
          { value: MealTypeEnum.Lunch, label: 'Lunch' },
        ];
  };

  render() {
    const {
      loading,
      loadingServedItems,
      servedItemOptions,
      change,
      resetSection,
      loadingCalculation,
    } = this.props;

    const { membershipValidate } = this.state;

    return (
      <Form>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <SiteComponent
                name={Fields.SiteId}
                isForm
                fluid
                onChange={(siteId) => {
                  this.resetParticipationValidation();
                  if (this.isSiteBreakfastOnly(siteId)) {
                    change(Fields.MealType, MealTypeEnum.Breakfast);
                  }
                  change(Fields.SiteId, siteId);
                  resetSection(Fields.ItemId);
                }}
                isAllRequired={false}
                alternateProvisionTypes={[
                  AlternateProvisionTypeEnum.Provision2_Non_Base,
                  AlternateProvisionTypeEnum.Prov2_Non_Base_Breakfast_Only,
                ]}
                isPrimary
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}>
              <Field
                label="Date"
                name={Fields.ServingDate}
                component={DatePickerField}
                type="text"
                openToDate={currentDate}
                maxDate={currentDate}
                fluid
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <ReduxFormButtonGroup
                label="Meal Type"
                formName={PROVISION2_FORM_NAME}
                options={this.getMealTypeOptions()}
                fieldName={Fields.MealType}
                onChange={() => {
                  resetSection(
                    Fields.ItemId,
                    Fields.Free,
                    Fields.Reduced,
                    Fields.Paid
                  );
                }}
                fluid
                showOnlyTouchedError
              />
            </Grid.Column>
            <Grid.Column width={8}>
              <Field
                name={Fields.ItemId}
                component={Select}
                fluid={true}
                options={servedItemOptions}
                label="Reimbursable Item"
                loading={loadingServedItems}
                disabled={loadingServedItems}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}>
              <Field
                name={Fields.ParticipationNumber}
                component={InputField}
                label={'Total Participation'}
                disabled={loading}
                mode={InputMode.Integer}
                maxLength={5}
                validate={membershipValidate}
                onChange={this.resetParticipationValidation}
                fluid
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                name={Fields.Free}
                component={InputField}
                label={'Free'}
                disabled
                fluid
                loading={loadingCalculation}
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                name={Fields.Reduced}
                component={InputField}
                label={'Reduced'}
                disabled
                fluid
                loading={loadingCalculation}
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                name={Fields.Paid}
                component={InputField}
                label={'Paid'}
                disabled
                fluid
                loading={loadingCalculation}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    );
  }
}

const requiredFieldsValidator = createFieldsValidator(
  [
    Fields.SiteId,
    Fields.ServingDate,
    Fields.MealType,
    Fields.ParticipationNumber,
    Fields.ItemId,
  ],
  {
    [Fields.SiteId]: 'Site is required.',
    [Fields.ItemId]: 'Reimbursable item is required.',
    [Fields.ParticipationNumber]: 'Total participation is required.',
    [Fields.ServingDate]: 'Please enter a valid serving date.',
  },
  {
    [Fields.MealType]: (value) =>
      value === MealTypeEnum.Breakfast || value === MealTypeEnum.Lunch,
    [Fields.ServingDate]: (value) => isDateValid(value),
  }
);

const calculationFieldsValidator = createFieldsValidator(
  [
    Fields.SiteId,
    Fields.ServingDate,
    Fields.MealType,
    Fields.ParticipationNumber,
  ],
  { [Fields.ServingDate]: 'Please enter a valid serving date.' },
  {
    [Fields.MealType]: (value) =>
      value === MealTypeEnum.Breakfast || value === MealTypeEnum.Lunch,
    [Fields.ServingDate]: (value) => isDateValid(value),
  }
);

const reimbursableItemFieldsValidator = createFieldsValidator(
  [Fields.SiteId, Fields.MealType, Fields.ServingDate],
  { [Fields.ServingDate]: 'Please enter a valid serving date.' },
  {
    [Fields.MealType]: (value) =>
      value === MealTypeEnum.Breakfast || value === MealTypeEnum.Lunch,
    [Fields.ServingDate]: (value) => isDateValid(value),
  }
);

const validateFiscalYearDT = (value, date) => {
  const dt = moment(value);

  if (dt.isAfter(currentDate)) {
    return 'Future Date is not permitted.';
  }

  const actualDT = moment(date);

  if (dt.isBefore(actualDT)) {
    return `Date cannot be prior to ${actualDT.format(DATE_FORMAT_FOR_UI)}`;
  }
};

const validate = (values, props) => {
  const errors = {};
  const requiredErrors = requiredFieldsValidator(values);

  if (!requiredErrors[Fields.ServingDate] && props.controlInfoDT) {
    errors[Fields.ServingDate] = validateFiscalYearDT(
      values[Fields.ServingDate],
      props.controlInfoDT
    );
  }

  return {
    ...errors,
    ...requiredErrors,
  };
};

const getServedItemTriggers = [
  Fields.MealType,
  Fields.SiteId,
  Fields.ServingDate,
];

const getCalculationTriggers = [
  Fields.MealType,
  Fields.SiteId,
  Fields.ServingDate,
  Fields.ItemId,
  Fields.ParticipationNumber,
];

const onChange = (values, dispatch, props, previousValues) => {
  const servedItemTriggerValues = _.pick(values, getServedItemTriggers);
  const prevServedItemTriggerValues = _.pick(
    previousValues,
    getServedItemTriggers
  );

  const servingDTError = validateFiscalYearDT(
    values[Fields.ServingDate],
    props.controlInfoDT
  );

  if (!_.isEqual(servedItemTriggerValues, prevServedItemTriggerValues)) {
    const errors = reimbursableItemFieldsValidator(values);
    if (!hasFormErrors(errors) && !servingDTError) {
      props.getServedItems(values[Fields.SiteId], values[Fields.MealType]);
    }
  }

  const calculationTriggers = _.pick(values, getCalculationTriggers);
  const prevCalculationTriggers = _.pick(
    previousValues,
    getCalculationTriggers
  );

  // if there are any changes, get new calculation
  if (!_.isEqual(calculationTriggers, prevCalculationTriggers)) {
    const requiredErrors = calculationFieldsValidator(values);
    // no errors found, all required fields are filled
    if (!hasFormErrors(requiredErrors) && !servingDTError) {
      const data = _.pick(values, [
        Fields.SiteId,
        Fields.ServingDate,
        Fields.MealType,
        Fields.ParticipationNumber,
      ]);
      props.getCalculation.cancel();
      props.getCalculation(data);
    }
  }

  if (!values[Fields.ParticipationNumber]) {
    props.resetSection(Fields.Free, Fields.Reduced, Fields.Paid);
  }
};

Provision2Form = reduxForm({
  form: PROVISION2_FORM_NAME,
  validate,
  onChange,
})(Provision2Form);

const selector = formValueSelector(PROVISION2_FORM_NAME);

const mapStateToProps = (state) => ({
  servedItemOptions: Provision2Selectors.getServedItemOptions(state),
  loadingServedItems: Provision2Selectors.getLoadingServedItems(state),
  provision2Error: Provision2Selectors.getError(state),
  loadingCalculation: Provision2Selectors.getLoadingCalculation(state),
  sitesMap: CommonStateSelectors.getSitesMap(state),
  siteId: selector(state, Fields.SiteId),
});

const mapDispatchToProps = {
  getServedItems: Provision2Actions.getServedItems,
  resetError: Provision2Actions.resetError,
};

Provision2Form = connect(mapStateToProps, mapDispatchToProps)(Provision2Form);

export { Provision2Form };



// WEBPACK FOOTER //
// ./src/pages/provision2/components/Provision2Form.js